import * as RadixTooltip from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';

type Side = 'top' | 'right' | 'bottom' | 'left';
type Align = 'start' | 'center' | 'end';

type TooltipProps = {
  content: ReactNode;
  children: ReactNode;
  side?: Side;
  align?: Align;
  /**
   * Override the default delay (300ms, configured on the app-root Provider).
   * Most call sites should leave this alone — older audience benefits from
   * the consistent pause.
   */
  delayDuration?: number;
  /** Render the trigger inline (no wrapper element). The child must accept ref + standard event handlers. */
  asChild?: boolean;
};

/**
 * App-root tooltip context. Mount once near the top of the tree (in `app.tsx`
 * around RouterProvider). DO NOT wrap individual triggers in their own
 * Provider — doing so creates per-trigger context state and makes virtualized
 * tables (thousands of cells) re-render expensively on scroll.
 */
export const TooltipProvider = RadixTooltip.Provider;

export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  delayDuration,
  asChild = true,
}: TooltipProps) {
  if (content === null || content === undefined || content === '') {
    return <>{children}</>;
  }

  return (
    <RadixTooltip.Root delayDuration={delayDuration}>
      <RadixTooltip.Trigger asChild={asChild}>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={8}
          className="z-tooltip max-w-xs rounded-md bg-tooltip px-2 py-1.5 text-xs font-medium text-tooltip-foreground shadow-soft"
        >
          {content}
          <RadixTooltip.Arrow className="fill-tooltip" width={10} height={5} />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}
